import type { ComputedRef } from 'vue'
import type { CustomerActiveSubscription } from '@entities/customer'
import type { SubscriptionModal } from '@entities/subscription'

import { useCustomerStore } from '@entities/customer'
import { useToast } from '@shared/ui/toast'

import { useCustomerPrivileges } from './customer-privileges.hook'
import { useCustomerRefresh } from './customer-refresh.hook'
import { useCustomerSubscriptions } from './customer-subscriptions.hook'
import { useCustomerUpdateApi } from '../api/update.hook'

interface SectionCustomerGeneralReturn {
  refTitle: ComputedRef<string>
  hasCustomer: ComputedRef<boolean>
  hasDetails: ComputedRef<boolean>
  isProcessing: ComputedRef<boolean>
  canViewCustomers: ComputedRef<boolean>
  canManageCustomers: ComputedRef<boolean>
  refSubscriptions: ComputedRef<CustomerActiveSubscription[]>
  activeSubscriptionModal: ComputedRef<SubscriptionModal | null>
  canManageSubscriptions: ComputedRef<boolean>
  save: () => void
}

export const useSectionCustomerGeneral = (): SectionCustomerGeneralReturn => {
  const toast = useToast()
  const customerStore = useCustomerStore()
  const { runWithPageLoader, refreshCustomer } = useCustomerRefresh()
  const { canViewCustomers, canManageCustomers } = useCustomerPrivileges()
  const {
    refSubscriptions,
    activeModal: activeSubscriptionModal,
    canManageSubscriptions,
  } = useCustomerSubscriptions()

  const refTitle = computed<string>(() => {
    const customer = customerStore.getDetails ?? customerStore.getCustomer

    return customer ? `#${customer.id} ${customer.first_name}` : ''
  })
  const hasCustomer = computed<boolean>(() => customerStore.hasCustomer || customerStore.hasDetails)
  const hasDetails = computed<boolean>(() => customerStore.hasDetails)
  const isProcessing = computed<boolean>(() => customerStore.isProcessing)

  const submit = async (): Promise<void> => {
    const details = customerStore.getDetails
    const form = customerStore.getForm
    if (!details || !form) return

    if (!form.first_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.warning({ title: 'Warning!', description: 'You need to fill all the required fields.' })
      return
    }

    await runWithPageLoader(async () => {
      const isUpdated = await useCustomerUpdateApi({ customerId: details.id, payload: { ...form } })

      if (isUpdated) {
        await refreshCustomer(details.id)
      }
    })
  }

  const save = (): void => {
    void submit()
  }

  return {
    refTitle,
    hasCustomer,
    hasDetails,
    isProcessing,
    canViewCustomers,
    canManageCustomers,
    refSubscriptions,
    activeSubscriptionModal,
    canManageSubscriptions,
    save,
  }
}
